import { useEffect, useRef } from 'react';
import { useFeedStore } from '@/stores/feedStore';
import { useAuthStore } from '@/stores/authStore';
import type { SSEEvent } from '@/types/api';

const SSE_URL = '/events/feed';
const RECONNECT_DELAY = 3_000;

export function RegulatoryFeedProvider({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const { user } = useAuthStore();
  const { addEvent, setConnected, setError } = useFeedStore();
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) {
      setConnected(false);
      return;
    }

    let es: EventSource | null = null;

    function connect() {
      es = new EventSource(SSE_URL, { withCredentials: true });

      es.onopen = () => {
        setConnected(true);
        setError(null);
      };

      es.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data as string) as SSEEvent;
          addEvent(parsed);
        } catch {
          // ignore malformed events
        }
      };

      es.onerror = () => {
        if (es?.readyState === EventSource.CLOSED) {
          setConnected(false);
          setError('Connection lost. Reconnecting...');
          es?.close();
          reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY);
        }
        // If CONNECTING (readyState === 0), browser is auto-retrying — don't disconnect yet
      };
    }

    connect();

    return () => {
      es?.close();
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      setConnected(false);
    };
  }, [user, addEvent, setConnected, setError]);

  return <>{children}</>;
}
